import { useMutation } from '@apollo/client';
import { CardDetails } from '@baemingo/adyen-terminal-comms';
import { sumBy } from 'lodash';

import { useReportsApi } from '~Components/ReportsUniverse';
import {
  ReceiptType,
  useDispatch,
  useI18n,
  useLogger,
  useReceiptGenerator,
  useSelector,
} from '~Hooks';
import { addToast } from '~actions';
import {
  EmailOrderReceipt,
  EmailOrderReceiptVariables,
  SetOrderHasPrinted,
  SetOrderHasPrintedVariables,
} from '../Scenes/Handy/__queries__';
import { EMAIL_ORDER_RECEIPT, SET_ORDER_HAS_PRINTED } from '../Scenes/Handy/queries';
import { useStorage } from '~Components/Storage';
import { PrintRow } from '~Components/PrinterManager';
import { ROLE } from '../../__queries__';
import { GetOrder_order } from '~Scenes/History-v2/Components/__queries__';

export interface PrintReceiptArgs {
  type: ReceiptType;
  order: GetOrder_order;
  itemIds?: string[];
  cardDetails?: CardDetails;
  cardIdentifier?: string;
  cardMethod?: string;
  isCopy?: boolean;
}

export const useReceiptCopy = () => {
  const [storage] = useStorage();
  const venueId = useSelector((state) => state.venueId);
  const receiptCounter = useSelector((state) => state.POSReceiptCounter);
  const flags = useSelector((state) => state.companyFlags);
  const country = useSelector((state) => state.country);

  const generateReceipt = useReceiptGenerator({ venueId: venueId || '' });
  const dispatch = useDispatch();
  const { i18n } = useI18n();
  const reportsApi = useReportsApi();
  const logger = useLogger('receipt-copy');

  const [setOrderHasPrinted] = useMutation<SetOrderHasPrinted, SetOrderHasPrintedVariables>(
    SET_ORDER_HAS_PRINTED,
  );
  const [emailOrderReceipt] = useMutation<EmailOrderReceipt, EmailOrderReceiptVariables>(
    EMAIL_ORDER_RECEIPT,
  );

  const getReceiptItems = (order: GetOrder_order, itemIds?: string[]) =>
    order.items
      .filter((item) => !item.isRefunded)
      .filter((item) => {
        // If no itemIds are passed, its a full order receipt/refund
        // and we want all items
        if (!itemIds?.length) {
          return true;
        }
        // If itemIds are passed, its a partial receipt/refund
        // and we only want the items that are in the itemIds array
        return itemIds.includes(item.id);
      })
      .map(({ entry, price, fraction, variations, removedVariations }) => ({
        entryId: entry.id,
        amount: price.amount,
        discount: price.discount,
        percentageVAT: price.percentageVAT,
        variations: variations?.map((variation) => i18n(variation.nameLang)),
        removedVariations: removedVariations?.map((variation) => i18n(variation.nameLang)),
        title: i18n(entry.item.titleLang),
        fraction,
      }));

  const printReceiptCopy = async ({
    type,
    order,
    itemIds,
    isCopy,
  }: PrintReceiptArgs): Promise<void> => {
    const [
      _complianceId = undefined,
      _receiptCounter = undefined,
      deviceRegNumber,
      deviceId,
    ] = order.basketToken.split('-');
    const items = getReceiptItems(order, itemIds);

    const amount = sumBy(items, (item) => item.amount - item.discount);
    const total = type === ReceiptType.Refund ? -amount : amount;
    const tip =
      type === ReceiptType.Refund
        ? -(order.payment.correctedTip ?? order.payment.tip)
        : order.payment.correctedTip ?? order.payment.tip;
    const totalDiscount = sumBy(items, (item) => item.discount);

    let userId: string | undefined = undefined;
    const isBackofficeAdmin =
      order?.user.role === ROLE.CONTENT_ADMIN || (order?.user.venues ?? []).length > 0;

    if (!isBackofficeAdmin && country === 'ES') {
      userId = order?.user?.id;
    }

    const receipt = await generateReceipt({
      items,
      totalDiscount,
      type,
      isCopy,
      cardDetails: order?.payment?.metadata?.cardDetails,
      payments: [
        {
          type: order.payment.type,
          amount: (total / 100).toFixed(),
        },
      ],
      extraVars: {
        tip: itemIds?.length ? undefined : tip.toString(),
        paymentIncTip: itemIds?.length ? undefined : (total + tip).toFixed(2),
        checkId: (receiptCounter + 1).toString() ?? '0',
        itemsTotal: (total / 100).toFixed(2),
        deviceId,
        deviceRegNumber,
        datetime: order.createdAt,
      },
      userId,
    });

    if (receipt) {
      const message: PrintRow[] = receipt.lines.map((line) => ({
        fontSize: line.fontSize || (line.doubleHeigth || line.doubleWidth ? 26 : 13), // doubleHeight here can come from the receipt package
        content: line.text,
      }));

      const QRContent =
        flags.hasPoints && !storage.printQROnCheque
          ? `claim:${order.payment.transactionId}:${total}:org_8AGDOueNZxuxTJ0PfaZk5`
          : undefined;

      if (QRContent) {
        message.push({ content: QRContent, qr: true });
      }

      window.printerManager?.initiatePrint({
        data: message,
        printType: 'RECEIPT',
        base64Image: receipt.base64Image,
      });

      // Increase receipt counter
      dispatch({
        type: 'INCREASE_RECEIPT_COUNTER',
      });

      const orderId = order.id;

      reportsApi.receiptCopyPrinted(amount);

      setOrderHasPrinted({
        variables: { id: orderId, hasPrintedReceiptCopy: true },
      });
    }
  };

  const emailReceiptCopy = async ({ order, email }: { order: GetOrder_order; email?: string }) => {
    try {
      const result = await emailOrderReceipt({
        variables: { orderId: order.id, email: email || order.user.email },
      });

      if (result.data?.sendOrderEmail?.error && result.data?.sendOrderEmail?.message) {
        throw new Error(result.data.sendOrderEmail.message);
      }

      const amount = sumBy(order.items, (item) => item.price.amount - item.price.discount);

      reportsApi.receiptCopyEmailed(amount);

      dispatch(addToast(`${i18n('TOAST.Email.Sent')} ${email || order.user.email}`, true));

      setOrderHasPrinted({
        variables: { id: order.id, hasPrintedReceiptCopy: true },
      });
    } catch (e) {
      logger.error(`Error sending receipt copy email`, {
        errorMessage: e,
        order,
      });
      dispatch(addToast(i18n('TOAST.Email.Not.Sent'), true));
    }
  };

  return { printReceiptCopy, emailReceiptCopy };
};
